/**
 * SplitView — a layout container whose side panel is a REAL FLEX SIBLING of
 * the main content, not a body-portaled overlay.
 *
 * Because the two regions share one flex row, the main area's right edge IS
 * the panel's left edge: a gap between them is geometrically impossible, in
 * any state, at any point of any animation. This is the sanctioned way to
 * render a panel "beside" content (the action detail page's entity panel,
 * the builder's record panel) — never reserve width against a floating
 * overlay with `calc(100% - Npx)`, which requires keeping two independent
 * surfaces in sync and leaks whitespace whenever they drift.
 *
 * Open/close animates the panel column's width between 0 and `width`; the
 * panel CONTENT sits in an inner wrapper fixed at `width`, so it is revealed
 * / concealed by the sweep rather than reflowing every frame. The main
 * region does reflow with the sweep — that is what keeps the shared edge
 * glued.
 *
 * The panel is presence-animated: when `panel` flips to null the last
 * content is retained and swept out before unmounting.
 *
 * Pure layout primitive: content for both regions comes from the caller;
 * width is controlled (pass `width` + `onWidthChange`); the integrated
 * resize handle is optional (`resizable`). Responsive behavior is the
 * caller's decision — gate `panel` on a breakpoint and render your own
 * overlay below it (see BuilderChat / ActionDetailPage).
 */

import {
  type MouseEvent as ReactMouseEvent,
  type ReactNode,
  type TransitionEvent,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';

import { EdgeResizeHandle } from '@/lib/base/resize-handle/edge-resize-handle';
import { cx } from '@/utils/cx';

export interface SplitViewProps {
  /** Main region content (flex-1, min-w-0). */
  children: ReactNode;
  /** Panel content; null/undefined closes the panel (animated out). */
  panel?: ReactNode;
  /** Controlled panel width in px. */
  width: number;
  /** Receives the clamped width during/after a drag or keyboard resize. */
  onWidthChange?: (width: number) => void;
  /** Renders the drag/keyboard resize handle on the panel's inner edge. */
  resizable?: boolean;
  minWidth?: number;
  maxWidth?: number;
  /** Reports drag start/end (e.g. to persist width or coordinate cursors). */
  onResizingChange?: (isResizing: boolean) => void;
  /** Accessible name for the resize handle. */
  resizeHandleLabel?: string;
  /** Duration of the open/close sweep. 0 disables the animation. */
  animationMs?: number;
  'aria-label'?: string;
  className?: string;
  /** data-instance for e2e queries when a page hosts several split views. */
  instance?: string;
  /**
   * Which side the panel occupies. Defaults to `'right'` (the record-panel
   * case). `'left'` is the same machine mirrored — used for the actions-list
   * rail on the full-page action route, so a page can host a rail on one side
   * and a record panel on the other and both sweep identically.
   */
  side?: 'left' | 'right';
}

type PanelPhase = 'closed' | 'opening' | 'open' | 'closing';

const DEFAULT_MIN_WIDTH = 320;
const DEFAULT_MAX_WIDTH = 900;
const DEFAULT_ANIMATION_MS = 300;

export function SplitView({
  children,
  panel,
  width,
  onWidthChange,
  resizable = false,
  minWidth = DEFAULT_MIN_WIDTH,
  maxWidth = DEFAULT_MAX_WIDTH,
  onResizingChange,
  resizeHandleLabel = 'Resize panel',
  animationMs = DEFAULT_ANIMATION_MS,
  'aria-label': ariaLabel,
  className,
  instance,
  side = 'right',
}: SplitViewProps) {
  const hasPanel = panel != null;

  // ── Presence machine: closed → opening → open → closing → closed ──────
  const [phase, setPhase] = useState<PanelPhase>(hasPanel ? 'open' : 'closed');
  // Retain the last panel content while sweeping out.
  const lastPanelRef = useRef<ReactNode>(panel);
  if (hasPanel) {
    lastPanelRef.current = panel;
  }

  useLayoutEffect(() => {
    if (hasPanel) {
      if (phase === 'closed' || phase === 'closing') {
        setPhase(animationMs > 0 ? 'opening' : 'open');
      }
    } else if (phase === 'open' || phase === 'opening') {
      setPhase(animationMs > 0 ? 'closing' : 'closed');
    }
  }, [hasPanel, phase, animationMs]);

  // 'opening' paints one frame at width 0, then advances to 'open' so the
  // width transition sweeps the panel in. Double-RAF: the first frame can
  // coalesce with the mount commit, which would skip the 0-width starting
  // keyframe.
  useEffect(() => {
    if (phase !== 'opening') {
      return;
    }
    let raf2: number | null = null;
    const raf1 = requestAnimationFrame(() => {
      raf2 = requestAnimationFrame(() => setPhase('open'));
    });
    return () => {
      cancelAnimationFrame(raf1);
      if (raf2 !== null) {
        cancelAnimationFrame(raf2);
      }
    };
  }, [phase]);

  // 'closing' unmounts once the sweep-out completes (transitionend, with a
  // timeout fallback in case the transition never fires, e.g. reduced
  // motion or a display:none ancestor).
  useEffect(() => {
    if (phase !== 'closing') {
      return;
    }
    const timer = setTimeout(() => setPhase('closed'), animationMs + 100);
    return () => clearTimeout(timer);
  }, [phase, animationMs]);
  const handleTransitionEnd = useCallback((e: TransitionEvent) => {
    if (e.propertyName !== 'width' || e.target !== e.currentTarget) {
      return;
    }
    setPhase(current => (current === 'closing' ? 'closed' : current));
  }, []);

  // ── Drag / keyboard resize (controlled width via onWidthChange) ────────
  const [isResizing, setIsResizing] = useState(false);
  const dragStateRef = useRef<{ startX: number; startWidth: number } | null>(
    null
  );
  const clamp = useCallback(
    (w: number) => Math.min(maxWidth, Math.max(minWidth, w)),
    [minWidth, maxWidth]
  );
  // Refs mirror the latest callbacks so the document-level listeners
  // installed at drag start never go stale.
  const onWidthChangeRef = useRef(onWidthChange);
  onWidthChangeRef.current = onWidthChange;
  const clampRef = useRef(clamp);
  clampRef.current = clamp;
  const onResizingChangeRef = useRef(onResizingChange);
  onResizingChangeRef.current = onResizingChange;
  const sideRef = useRef(side);
  sideRef.current = side;

  const handleResizeStart = useCallback(
    (e: ReactMouseEvent) => {
      e.preventDefault();
      dragStateRef.current = { startX: e.clientX, startWidth: width };
      setIsResizing(true);
      onResizingChangeRef.current?.(true);
      document.body.classList.add('col-resize-active');

      // rAF-throttled drag that always applies the LATEST pointer position:
      // `pending` is flipped before scheduling so a synchronous rAF (tests)
      // can't wedge the throttle shut, and `latestClientX` keeps fast moves
      // from being applied out of date.
      let frame: number | null = null;
      let pending = false;
      let latestClientX = e.clientX;
      const handleMouseMove = (move: MouseEvent) => {
        latestClientX = move.clientX;
        if (pending) {
          return;
        }
        pending = true;
        frame = requestAnimationFrame(() => {
          pending = false;
          frame = null;
          const drag = dragStateRef.current;
          if (!drag) {
            return;
          }
          // Right-anchored panel: dragging left grows it.
          // Left-anchored panel: delta is mirrored — dragging right grows it.
          const isLeft = sideRef.current === 'left';
          const delta = isLeft
            ? latestClientX - drag.startX
            : drag.startX - latestClientX;
          const next = clampRef.current(drag.startWidth + delta);
          onWidthChangeRef.current?.(next);
        });
      };
      const handleMouseUp = () => {
        if (frame !== null) {
          cancelAnimationFrame(frame);
        }
        dragStateRef.current = null;
        setIsResizing(false);
        onResizingChangeRef.current?.(false);
        document.body.classList.remove('col-resize-active');
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    },
    [width]
  );

  const handleKeyboardResize = useCallback(
    (delta: number) => {
      onWidthChangeRef.current?.(clampRef.current(width + delta));
    },
    [width]
  );

  const panelVisible = phase !== 'closed';
  const panelWidth = phase === 'open' ? width : 0;
  const isLeft = side === 'left';

  const panelColumn = panelVisible ? (
    <div
      data-component='split-view-panel'
      data-state={phase}
      onTransitionEnd={handleTransitionEnd}
      className={cx(
        'relative h-full shrink-0 overflow-hidden bg-bg-primary',
        // The dividing border always sits on the panel's INNER edge — the one
        // facing the main region.
        isLeft
          ? 'border-r border-[var(--color-border-secondary)]'
          : 'border-l border-[var(--color-border-secondary)]',
        // Suppress the sweep transition while dragging so the panel
        // tracks the pointer 1:1.
        !isResizing &&
          animationMs > 0 &&
          'transition-[width] ease-out motion-reduce:transition-none'
      )}
      style={{
        width: `${panelWidth}px`,
        transitionDuration:
          !isResizing && animationMs > 0 ? `${animationMs}ms` : undefined,
      }}
    >
      {/* Inner wrapper pinned to the full width so the content is revealed by
          the sweep instead of reflowing each frame. Anchored to the panel's
          OUTER edge, so the sweep reveals from the inner edge inwards —
          right-anchored for a right panel, left-anchored for a left one. */}
      <div
        className={cx(
          'absolute inset-y-0 h-full',
          isLeft ? 'left-0' : 'right-0'
        )}
        style={{ width: `${width}px` }}
      >
        {lastPanelRef.current}
      </div>
      {resizable && phase === 'open' && (
        <EdgeResizeHandle
          edge={isLeft ? 'right' : 'left'}
          onResizeStart={handleResizeStart}
          onKeyboardResize={handleKeyboardResize}
          width={width}
          minWidth={minWidth}
          maxWidth={maxWidth}
          isResizing={isResizing}
          aria-label={resizeHandleLabel}
        />
      )}
    </div>
  ) : null;

  return (
    <div
      data-component='split-view'
      data-instance={instance}
      aria-label={ariaLabel}
      className={cx('flex h-full w-full overflow-hidden', className)}
    >
      {isLeft && panelColumn}
      {/* Main region — flex-1 keeps its edge glued to the panel's inner edge
          at every animation frame. */}
      <div className='flex-1 min-w-0 h-full overflow-hidden'>{children}</div>
      {!isLeft && panelColumn}
    </div>
  );
}
SplitView.displayName = 'SplitView';
